import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

export const useNotificationsStore = defineStore('notifications', () => {
  // State
  const notifications = ref([])
  const unreadCount = ref(0)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const hasNotifications = computed(() => notifications.value.length > 0)
  const hasUnread = computed(() => unreadCount.value > 0)
  
  const unreadNotifications = computed(() => 
    notifications.value.filter(n => !n.is_read)
  )
  
  const readNotifications = computed(() => 
    notifications.value.filter(n => n.is_read)
  )

  // Actions
  async function fetchNotifications() {
    loading.value = true
    error.value = null
    try {
      const response = await api.get('/api/v1/notifications')
      notifications.value = response.data.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch notifications'
    } finally {
      loading.value = false
    }
  }

  async function fetchUnreadCount() {
    try {
      const response = await api.get('/api/v1/notifications/unread-count')
      unreadCount.value = response.data.data.count
    } catch (err) {
      // Silently fail
    }
  }

  async function fetchNotification(id) {
    loading.value = true
    error.value = null
    try {
      const response = await api.get(`/api/v1/notifications/${id}`)
      return response.data.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch notification'
      return null
    } finally {
      loading.value = false
    }
  }

  async function markAsRead(id) {
    try {
      await api.put(`/api/v1/notifications/${id}/read`)
      
      const index = notifications.value.findIndex(n => n.id === id)
      if (index !== -1 && !notifications.value[index].is_read) {
        notifications.value[index].is_read = true
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
      
      return { success: true }
    } catch (err) {
      return { success: false, error: err.response?.data?.message }
    }
  }

  async function markAllAsRead() {
    try {
      await api.put('/api/v1/notifications/read-all')
      
      notifications.value.forEach(n => {
        n.is_read = true
      })
      unreadCount.value = 0
      
      return { success: true }
    } catch (err) {
      return { success: false, error: err.response?.data?.message }
    }
  }

  async function deleteNotification(id) {
    try {
      await api.delete(`/api/v1/notifications/${id}`)
      
      const notification = notifications.value.find(n => n.id === id)
      if (notification && !notification.is_read) {
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
      
      notifications.value = notifications.value.filter(n => n.id !== id)
      
      return { success: true }
    } catch (err) {
      return { success: false, error: err.response?.data?.message }
    }
  }

  // Add a local notification (for UI feedback)
  function addLocalNotification(notification) {
    notifications.value.unshift({
      id: `local-${Date.now()}`,
      is_read: false,
      created_at: new Date().toISOString(),
      ...notification
    })
    unreadCount.value++
  }

  function clearNotifications() {
    notifications.value = []
    unreadCount.value = 0
  }

  return {
    // State
    notifications,
    unreadCount,
    loading,
    error,
    // Getters
    hasNotifications,
    hasUnread,
    unreadNotifications,
    readNotifications,
    // Actions
    fetchNotifications,
    fetchUnreadCount,
    fetchNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addLocalNotification,
    clearNotifications
  }
})
